import { TFunction } from '@frontend/i18n';

interface BuildChartSubtitleParams {
  /**
   * Current Y-axis selections keyed by axis group key.
   * e.g. { leadTime: ['LEAD_TIME_0_1_DAYS'], atRiskStatus: ['AT_RISK_STATUS_REPEAT_CANCEL'] }
   */
  selectedYAxes: Record<string, string[]>;
  /** Flattened list of all Y-axis options (value → label). */
  yAxisOptions: Array<{ value: string; label: string }>;
  /**
   * Maps each axis group key to the suffix appended to its selected labels.
   * Object key order controls the display order of segments.
   * e.g. { atRiskStatus: 'risk status', leadTime: 'lead time' }
   */
  suffixMap: Record<string, string>;
  /** Human-readable date/range hint from the store, e.g. "Next 7 days". */
  filterHintText?: string;
  t: TFunction;
}

/**
 * Builds a natural-language subtitle sentence for charts that use ChartEditPanel.
 *
 * Examples:
 *  - both axes selected: "Next 7 days of Repeat cancel risk status, > 10 days lead time appointments"
 *  - only lead time:     "Next 7 days of > 10 days lead time appointments"
 *  - nothing selected:   "Next 7 days of appointments"
 *  - no filterHintText:  "Repeat cancel risk status, > 10 days lead time"
 */
export const buildChartSubtitle = ({
  selectedYAxes,
  yAxisOptions,
  suffixMap,
  filterHintText,
  t,
}: BuildChartSubtitleParams): string => {
  const getLabel = (value: string) => yAxisOptions.find((opt) => opt.value === value)?.label;

  const processedKeys = new Set<string>();

  const suffixedSegments = Object.entries(suffixMap)
    .map(([groupKey, suffix]) => {
      processedKeys.add(groupKey);
      const selectedValues = selectedYAxes[groupKey] ?? [];
      const labels = selectedValues.map(getLabel).filter(Boolean) as string[];
      return labels.length ? `${labels.join(', ')} ${t(suffix)}` : '';
    })
    .filter(Boolean);

  const remainingSegments = Object.entries(selectedYAxes)
    .filter(([groupKey]) => !processedKeys.has(groupKey))
    .map(([, selectedValues]) => {
      const labels = selectedValues.map(getLabel).filter(Boolean) as string[];
      return labels.join(', ');
    })
    .filter(Boolean);

  const segments = [...suffixedSegments, ...remainingSegments];

  const descriptor = segments.join(', ');

  if (filterHintText) {
    return descriptor
      ? `${filterHintText} ${t('of')} ${descriptor} ${t('appointments')}`
      : `${filterHintText} ${t('of')} ${t('appointments')}`;
  }

  return descriptor;
};
